import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import { I18nProvider } from "./lib/i18n";
import { installOnlineListener } from "./lib/outbox";
import {
  hideNativeSplash,
  initNativeKeyboard,
  initPushNotifications,
  isNative,
} from "./lib/native";
import "./styles/tailwind.css";

// Offline outbox installs an "online" listener that flushes any
// queued mutations as soon as the network comes back. Idempotent.
installOnlineListener();

// On iOS (Capacitor WKWebView shell) we own the native lifecycle:
// hide the launch splash once React paints, and ask Apple for a push
// device token. Both no-op on the web. initPushNotifications waits for
// the user to be signed in via the api wrapper, so calling here at
// boot is safe — if they're not signed in yet, the registration just
// happens later after they log in (the native bridge fires on every
// app open).
if (isNative()) {
  queueMicrotask(() => void hideNativeSplash());
  // Hide the iOS keyboard accessory bar (Prev/Next/Done) + switch to
  // native resize mode so sticky bottom bars stay above the keyboard.
  // Idempotent; safe to call before React mounts.
  void initNativeKeyboard();
  // Slight delay so the session cookie / /api/me check has a chance
  // to settle before we POST the token.
  setTimeout(() => void initPushNotifications(), 2000);
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <BrowserRouter>
        <I18nProvider>
          <App />
        </I18nProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  </React.StrictMode>,
);

// Tell index.html to hide the SEO fallback block. Fires after React's
// first render is queued — close enough to "user sees the app" that
// no flash is perceptible.
queueMicrotask(() => {
  document.dispatchEvent(new CustomEvent("fk:app-mounted"));
});

// Service worker REGISTRATION disabled + active-SW UNREGISTER on load.
//
// The SW at /sw.js was intercepting GET /api/* requests, running its own
// fetch, and network-first-caching the response body. For most endpoints
// that's fine — but for /api/auth/session it collided badly with the
// sign-in flow: a `{user: null}` response cached while signed out could
// re-render before the fresh cookie-authenticated response arrived,
// causing AppLayout to bounce the user back to /signin immediately after
// a successful sign-in. Symptom: user clicks Sign In, no error appears,
// but they land back on /signin. (User report: "everytime i click sign in
// it just asks me to sign in again with no error".)
//
// The magic-link login path worked fine because it was a full-page 302
// redirect that goes through the SW's navigate handler, which passes
// through without cache-mutating /api/auth/session.
//
// Rather than surgically fix the SW's /api/auth/* caching (fragile —
// there are other cookie-sensitive endpoints that could hit the same
// class of bug), remove the SW entirely for now. Offline reads still
// work via the IDB cache layer in src/frontend/lib/idb-cache.ts.
//
// Actively unregister any previously-installed SW so returning users
// aren't stuck with the old one after this deploy — the SW would keep
// serving cached responses until the user cleared site data. This one
// call per page load makes the recovery automatic.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((regs) => {
      for (const reg of regs) {
        void reg.unregister();
      }
    })
    .catch(() => {
      /* best-effort — nothing to do if the browser blocks SW APIs */
    });
}
